import React, { useEffect, useMemo, useState } from 'react';

export interface ActiveProductItem {
  product_id: number;
  sku: string;
  product_name?: string | null;
  product_alias?: string | null;
  product_display_name?: string | null;
  group_id?: number | null;
  group_name?: string | null;
  is_selectable?: boolean;
  is_active?: boolean;
  system_excluded?: boolean;
}

interface Props {
  open: boolean;
  products: ActiveProductItem[];
  initialActiveSkus: string[];
  saving: boolean;
  activeCount: number;
  totalCount: number;
  onClose: () => void;
  onSave: (activeSkus: string[]) => Promise<void>;
}

const chipClass = (active: boolean) =>
  `shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition ${
    active
      ? 'border-slate-900 bg-slate-900 text-white'
      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
  }`;

const ActiveProductsModal: React.FC<Props> = ({
  open,
  products,
  initialActiveSkus,
  saving,
  activeCount,
  totalCount,
  onClose,
  onSave,
}) => {
  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);
  const [groupFilter, setGroupFilter] = useState<string>('all');

  useEffect(() => {
    if (!open) return;
    setSelectedSkus(initialActiveSkus);
    setGroupFilter('all');
  }, [initialActiveSkus, open]);

  const selectableProducts = useMemo(
    () =>
      products
        .filter(item => Boolean(item.is_selectable))
        .sort((a, b) =>
          String(a.product_display_name || a.product_alias || a.product_name || a.sku).localeCompare(
            String(b.product_display_name || b.product_alias || b.product_name || b.sku),
            'ru'
          )
        ),
    [products]
  );

  const groups = useMemo(() => {
    const map = new Map<string, { key: string; label: string; activeCount: number; totalCount: number }>();
    selectableProducts.forEach(item => {
      const key = item.group_id ? `group:${item.group_id}` : 'group:none';
      const label = String(item.group_name || '').trim() || 'Без группы';
      const bucket = map.get(key) || { key, label, activeCount: 0, totalCount: 0 };
      bucket.totalCount += 1;
      if (initialActiveSkus.includes(item.sku)) {
        bucket.activeCount += 1;
      }
      map.set(key, bucket);
    });
    return [{ key: 'all', label: 'Все', activeCount, totalCount }, ...Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'ru'))];
  }, [activeCount, initialActiveSkus, selectableProducts, totalCount]);

  const visibleProducts = useMemo(() => {
    if (groupFilter === 'all') return selectableProducts;
    if (groupFilter === 'group:none') {
      return selectableProducts.filter(item => !item.group_id);
    }
    const groupId = Number(groupFilter.replace('group:', ''));
    return selectableProducts.filter(item => Number(item.group_id || 0) === groupId);
  }, [groupFilter, selectableProducts]);

  const selectedSet = useMemo(() => new Set(selectedSkus), [selectedSkus]);
  const visibleAllSelected = visibleProducts.length > 0 && visibleProducts.every(item => selectedSet.has(item.sku));

  const toggleSku = (sku: string) => {
    setSelectedSkus(current =>
      current.includes(sku) ? current.filter(item => item !== sku) : [...current, sku]
    );
  };

  const toggleVisible = () => {
    const visibleSkus = visibleProducts.map(item => item.sku);
    setSelectedSkus(current => {
      const currentSet = new Set(current);
      if (visibleSkus.every(sku => currentSet.has(sku))) {
        return current.filter(sku => !visibleSkus.includes(sku));
      }
      visibleSkus.forEach(sku => currentSet.add(sku));
      return Array.from(currentSet);
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
      <div className="w-full max-w-4xl rounded-3xl bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-2xl font-semibold text-slate-900">Активные товары</h3>
              <p className="mt-1 text-sm text-slate-500">
                Активные SKU участвуют в выгрузках, AI агенте, срезах, рекомендациях поставок и отображении на дашборде.
              </p>
              <div className="mt-3 text-sm font-medium text-slate-700">
                Активно SKU: <span className="text-slate-900">{selectedSkus.length}</span> из <span className="text-slate-900">{totalCount}</span>
              </div>
            </div>
            <button
              type="button"
              className="rounded-full border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
              onClick={onClose}
              disabled={saving}
            >
              Закрыть
            </button>
          </div>

          <div className="mt-4 overflow-x-auto">
            <div className="flex min-w-max gap-2 pb-1">
              {groups.map(group => (
                <button
                  key={group.key}
                  type="button"
                  className={chipClass(groupFilter === group.key)}
                  onClick={() => setGroupFilter(group.key)}
                >
                  {group.label}
                  {group.key !== 'all' ? ` · ${group.activeCount}/${group.totalCount}` : ''}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-h-[58vh] overflow-y-auto px-6 py-4">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left hover:border-slate-300"
            onClick={toggleVisible}
          >
            <div>
              <div className="text-sm font-semibold text-slate-900">Выбрать все</div>
              <div className="text-xs text-slate-500">
                {visibleAllSelected ? 'Снять выбор со всех товаров в текущем списке' : 'Активировать все товары в текущем списке'}
              </div>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
              checked={visibleAllSelected}
              onChange={() => toggleVisible()}
            />
          </button>

          <div className="mt-3 space-y-2">
            {visibleProducts.map(item => {
              const checked = selectedSet.has(item.sku);
              return (
                <label
                  key={item.sku}
                  className={`flex cursor-pointer items-start justify-between gap-4 rounded-2xl border px-4 py-3 ${
                    checked ? 'border-emerald-300 bg-emerald-50/60' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-900">
                      {item.product_display_name || item.product_alias || item.product_name || item.sku}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      SKU: {item.sku}
                      {item.group_name ? ` · Группа: ${item.group_name}` : ''}
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                    checked={checked}
                    onChange={() => toggleSku(item.sku)}
                  />
                </label>
              );
            })}
            {visibleProducts.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
                В этой группе нет товаров.
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-6 py-4">
          <div className="text-xs text-slate-500">
            Если в группе не останется активных SKU, она перестанет участвовать в витринах и AI-срезах.
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              onClick={onClose}
              disabled={saving}
            >
              Отмена
            </button>
            <button
              type="button"
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
              onClick={() => onSave(selectedSkus)}
              disabled={saving}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveProductsModal;
